import React, {useEffect, useState} from "react";

// --- demo-local identity: localStorage token, sent explicitly. NO cookies.
// (Kept separate from the show's jfd_token. See docs/demo-draw-game.md.)
export const TOKEN_KEY = 'jfd_demo_token';
export const getToken = () => { try { return localStorage.getItem(TOKEN_KEY) || ''; } catch (e) { return ''; } };
export const saveToken = (t) => { try { localStorage.setItem(TOKEN_KEY, t); } catch (e) {} };
export const clearToken = () => { try { localStorage.removeItem(TOKEN_KEY); } catch (e) {} };

export const demoFetch = (path, options = {}) => {
    const token = getToken();
    const sep = path.includes('?') ? '&' : '?';
    const url = token ? `${path}${sep}demo_token=${encodeURIComponent(token)}` : path;
    return fetch(url, options);
};

export const post = (path, body) => demoFetch(path, {
    method: 'POST',
    headers: {'Content-Type': 'application/json'},
    body: JSON.stringify(body || {}),
});

export const getJSON = async (path) => {
    const res = await demoFetch(path);
    if (!res.ok) return null;
    return res.json();
};

// Host: mint a fresh coded game (the old token, if any, tells the server to throw
// away that host's previous game) and navigate to it as host.
export const startFreshDemo = async (history, mode = 'draw_off') => {
    const res = await post('/demo/init', {mode});
    if (!res.ok) return res.status === 429 ? 'resting' : false;
    const j = await res.json();
    saveToken(j.token);
    history.push(`/demo/${j.code}`);
    return true;
};

// === show design tokens (extracted from the real app) ===
export const FONT = "'Gabarito', sans-serif";
export const GREEN = '#36ff36';

// Zed's materials, kept minimal: brass hairlines, dark steel, a cold glow from above.
export const BRASS = '#b08d57';
export const BG = 'radial-gradient(120% 60% at 50% -8%, rgba(64,104,110,0.38) 0%, rgba(18,24,26,0.6) 45%, #050505 75%)';

export const page = {
    minHeight: '100dvh', boxSizing: 'border-box',
    padding: 'calc(20px + env(safe-area-inset-top)) 18px calc(24px + env(safe-area-inset-bottom))',
    display: 'flex', flexDirection: 'column', gap: 16,
    color: '#fff', background: `${BG}, #050505`, fontFamily: FONT,
};

// Text inputs: a dark recessed slot with a brass hairline (focus glow lives in index.css, .demo-input).
export const inputStyle = {
    width: '100%', boxSizing: 'border-box', padding: '16px 18px', fontSize: 22, fontFamily: FONT, color: '#f3ead8',
    background: 'rgba(10,9,8,0.85)', border: `1.5px solid rgba(176,141,87,0.55)`, borderRadius: 12,
    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.8)', outline: 'none', textAlign: 'left',
};

// Panels (prompt, result and choice boxes): dark steel with a brass hairline.
export const card = {border: '1.5px solid rgba(176,141,87,0.55)', borderRadius: 12, background: 'rgba(16,13,11,0.92)', color: '#f3ead8',
    boxShadow: '0 8px 24px rgba(0,0,0,0.5)'};

// The show's marquee CTA: the green glowing Join/Ready button.
export const greenBtn = (active = false) => ({
    width: '100%', padding: '24px 20px', fontSize: 30, fontWeight: 900, fontStyle: 'italic',
    letterSpacing: 3, textTransform: 'uppercase', fontFamily: FONT,
    color: active ? '#0a3a0a' : '#062406', backgroundColor: active ? '#9be59b' : GREEN,
    border: 'none', borderRadius: 14, cursor: active ? 'default' : 'pointer',
    boxShadow: active
        ? 'inset 0 1px 0 rgba(255,255,255,0.6), inset 0 -2px 0 rgba(0,0,0,0.35), 0 0 18px rgba(155,229,155,0.45)'
        : 'inset 0 1px 0 rgba(255,255,255,0.7), inset 0 -3px 0 rgba(0,0,0,0.45), 0 0 28px rgba(54,255,54,0.6), 4px 4px 0 rgba(0,0,0,0.5)',
    textShadow: '1px 1px 0 rgba(255,255,255,0.25)',
});

// Calmer primary button for the landing (the full green pulse CTA was too much).
export const primaryBtn = {
    width: '100%', padding: '17px', fontSize: 20, fontWeight: 800, fontFamily: FONT, letterSpacing: 3, textTransform: 'uppercase',
    color: '#1c1307', background: 'linear-gradient(#d6b072, #9c7642)', border: 'none', borderRadius: 12, cursor: 'pointer',
    boxShadow: 'inset 0 1px 0 rgba(255,240,210,0.6), inset 0 -2px 0 rgba(0,0,0,0.25), 0 4px 18px rgba(214,176,114,0.25)',
};

// Secondary button: a dark slot with a brass hairline.
export const darkBtn = {
    width: '100%', padding: '16px', fontSize: 20, fontWeight: 700, fontFamily: FONT, letterSpacing: 1,
    color: '#f3ead8', background: 'rgba(16,13,11,0.92)', border: '1.5px solid rgba(176,141,87,0.55)', borderRadius: 12, cursor: 'pointer',
};
// In-game submit and choice buttons: the brass primary.
export const whiteBtn = primaryBtn;

export const ghostBtn = {...darkBtn, flex: 1, width: 'auto', padding: '12px 0', fontSize: 18};
export const rankAccent = (rank) => (rank === 1 ? '#f1d28b' : rank === 2 ? '#d8d8d8' : rank === 3 ? '#cd7f32' : '#fff');
const STROKE_FILTER = 'drop-shadow(0 0 1px black) drop-shadow(0 0 1px black) drop-shadow(0 0 1px black)';

// The show's dancelestrations drawing backdrop (used on draw + guess screens).
export const Backdrop = () => (
    <div style={{
        position: 'fixed', inset: 0, zIndex: -1, pointerEvents: 'none',
        backgroundImage: 'linear-gradient(rgb(221 160 53 / 25%), rgb(200 148 58)), url(/dancelestrations-assets/backdrop.webp)',
        backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat',
    }} />
);

// Fixed top-center countdown badge, color-coded by time left (matches the show).
// A thin bar along the top edge that drains as time runs out, colored like the clock. On its own (the pairing
// screen) it says "this moves on by itself" without taking any layout space.
// One bar per deadline: when the deadline changes (a new phase, the pairing hold), the old bar fades out and a
// fresh full one fades in, instead of the width animating back up.
// `calm`: nothing for the player to do while it runs (a hold), so it stays green instead of turning urgent.
export const TimeBar = ({endsAt, offset = 0, total = null, calm = false}) => {
    // `fresh` = replaced an earlier deadline on this screen (fades itself in); the first bar is left to the screen's
    // own transition
    const [bars, setBars] = useState(endsAt ? [{endsAt, leaving: false, fresh: false}] : []);
    useEffect(() => {
        setBars(prev => {
            if (prev.some(b => !b.leaving && b.endsAt === endsAt)) return prev;
            return [...prev.map(b => ({...b, leaving: true})), ...(endsAt ? [{endsAt, leaving: false, fresh: true}] : [])];
        });
        const t = setTimeout(() => setBars(prev => prev.filter(b => !b.leaving)), 400);
        return () => clearTimeout(t);
    }, [endsAt]);
    return <>{bars.map(b => <Bar key={b.endsAt} endsAt={b.endsAt} offset={offset} total={total} leaving={b.leaving} fresh={b.fresh} calm={calm} />)}</>;
};

const Bar = ({endsAt, offset, total, leaving, fresh, calm}) => {
    const [, force] = useState(0);
    const [span] = useState(() => total || Math.max(1000, endsAt - (Date.now() + offset)));
    useEffect(() => {
        const id = setInterval(() => force(n => n + 1), 100);
        return () => clearInterval(id);
    }, []);
    const ms = Math.max(0, endsAt - (Date.now() + offset));
    const color = calm ? '#4dff7a' : ms <= 10000 ? '#ff3a3a' : ms <= 30000 ? '#ffd24a' : '#4dff7a';
    return <div data-fly="fade" style={{position: 'fixed', top: 0, left: 0, right: 0, height: 4, zIndex: 20, background: 'rgba(255,255,255,0.08)',
        opacity: leaving ? 0 : 1, transition: 'opacity 350ms ease', animation: fresh && !leaving ? 'fade-in 350ms ease both' : undefined}}>
        <div style={{height: '100%', width: `${Math.min(1, ms / span) * 100}%`, background: color, boxShadow: `0 0 8px ${color}99`,
            transition: 'width 100ms linear'}} />
    </div>;
};

// Time left on a timed screen: the draining bar along the top edge the whole time, and for the last FINAL_S
// seconds the clock badge pops in over the top of the screen (no layout space taken), its number popping each tick.
const FINAL_S = 10;
export const Countdown = ({endsAt, offset = 0}) => {
    const [, force] = useState(0);
    useEffect(() => {
        if (!endsAt) return undefined;
        const id = setInterval(() => force(n => n + 1), 100);
        return () => clearInterval(id);
    }, [endsAt]);
    if (!endsAt) return null;
    const s = Math.ceil(Math.max(0, endsAt - (Date.now() + offset)) / 1000);
    return <>
        <TimeBar endsAt={endsAt} offset={offset} />
        {s > 0 && s <= FINAL_S && <div className="final-clock" style={{
            position: 'fixed', top: 'calc(10px + env(safe-area-inset-top))', left: '50%', zIndex: 30, pointerEvents: 'none',
            width: 66, height: 66, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(0,0,0,0.85)', border: '3px solid #ff3a3a', boxShadow: '0 0 16px #ff3a3a88',
        }}>
            <span key={s} className="final-count" style={{fontSize: 30, fontWeight: 800, color: '#ff3a3a', fontVariantNumeric: 'tabular-nums'}}>{s}</span>
        </div>}
    </>;
};

// Cutout portrait (bg-removed PNG) with the show's triple-stroke outline.
export const Portrait = ({player, size = 64, style}) => {
    const common = {width: size, height: size, objectFit: 'cover', objectPosition: 'top', flexShrink: 0, filter: STROKE_FILTER, ...style};
    if (player && player.portrait_url) return <img src={player.portrait_url} alt="" style={common} />;
    return <div style={{...common, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: size * 0.5, filter: 'none'}}>🎭</div>;
};

// Reusable 9-dot loader (global .lds-grid in index.css).
export const Spinner = () => (
    <div className="lds-grid"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
);
